export enum PostForMeAccountStatus {
  Connected = "connected",
  Disconnected = "disconnected"
}

export enum SocialPlatform {
  Facebook = "facebook",
  Instagram = "instagram",
  TikTok = "tiktok"
}

export interface PostForMeAccount {
  id: string;
  platform: SocialPlatform | string;
  username: string;
  status: PostForMeAccountStatus | string;
  externalId: string;
  user_id: string;
}

export type PlatformKey = SocialPlatform;
export type DraftTabKey = "draft";
export type TabKey = DraftTabKey | PlatformKey;

export type Placement = "timeline" | "reels" | "stories";

export type FacebookConfig = {
  platform: SocialPlatform.Facebook;
  placement: Placement;
  location: string;
  collaborators: string;
};

export type InstagramConfig = {
  platform: SocialPlatform.Instagram;
  placement: Placement;
  shareToFeed: boolean;
  location: string;
  collaborators: string;
};

export type TikTokConfig = {
  platform: SocialPlatform.TikTok;
  placement?: "reels";
  title?: string | null;
  privacyStatus?: TikTokPrivacyStatus;
  allowComment?: boolean;
  allowDuet?: boolean;
  allowStitch?: boolean;
  discloseYourBrand?: boolean;
  discloseBrandedContent?: boolean;
  isAiGenerated?: boolean;
  isDraft?: boolean;
  autoAddMusic?: boolean;
};

export type TikTokPrivacyStatus = "public" | "private";

export type PlatformConfiguration = FacebookConfig | InstagramConfig | TikTokConfig;

export type PlatformOverrideState = {
  caption?: string;
  captionTouched?: boolean;
  media?: string[];
  mediaTouched?: boolean;
  config: PlatformConfiguration;
  hasPhoto?: boolean;
  hasVideo?: boolean;
};

export type PlatformOverrides = Partial<Record<PlatformKey, PlatformOverrideState>>;

export const SUPPORTED_TABS: PlatformKey[] = [SocialPlatform.Facebook, SocialPlatform.Instagram, SocialPlatform.TikTok];

export function isPlatformKey(value: string): value is PlatformKey {
  return value === SocialPlatform.Facebook || value === SocialPlatform.Instagram || value === SocialPlatform.TikTok;
}

export const DEFAULT_CONFIGS: Record<PlatformKey, PlatformConfiguration> = {
  [SocialPlatform.Facebook]: {
    platform: SocialPlatform.Facebook,
    placement: "timeline",
    location: "",
    collaborators: ""
  },
  [SocialPlatform.Instagram]: {
    platform: SocialPlatform.Instagram,
    placement: "timeline",
    shareToFeed: false,
    location: "",
    collaborators: ""
  },
  [SocialPlatform.TikTok]: {
    platform: SocialPlatform.TikTok,
    placement: "reels",
    title: null,
    privacyStatus: "public",
    allowComment: true,
    allowDuet: true,
    allowStitch: true,
    discloseYourBrand: false,
    discloseBrandedContent: false,
    isAiGenerated: false,
    isDraft: false,
    autoAddMusic: true
  }
};

export type DraftState = {
  caption: string;
  media: string[];
  hasPhoto?: boolean;
  hasVideo?: boolean;
};

export type FormState = {
  draft: DraftState;
  platforms: PlatformOverrides;
  selectedAccountIds: string[];
};

export type TikTokPlatformPayload = {
  caption?: string;
  media?: { url: string }[];
  title?: string | null;
  privacyStatus?: TikTokPrivacyStatus;
  allowComment?: boolean;
  allowDuet?: boolean;
  allowStitch?: boolean;
  discloseYourBrand?: boolean;
  discloseBrandedContent?: boolean;
  isAiGenerated?: boolean;
  isDraft?: boolean;
  autoAddMusic?: boolean;
};

export type CreatePostBody = {
  caption: string;
  social_accounts: string[];
  media?: { url: string }[];
  platform_configurations?: {
    facebook?: {
      caption?: string;
      media?: { url: string }[];
      placement?: Placement;
      location?: string;
      collaborators?: string[];
    };
    instagram?: {
      caption?: string;
      media?: { url: string }[];
      placement?: Placement;
      share_to_feed?: boolean;
      location?: string;
      collaborators?: string[];
    };
    tiktok?: TikTokPlatformPayload;
  };
};

export const PLATFORM_LIMITS: Record<PlatformKey, number> = {
  [SocialPlatform.Facebook]: 64000,
  [SocialPlatform.Instagram]: 4000,
  [SocialPlatform.TikTok]: 2200
};

export function deriveSelectedPlatforms(accounts: PostForMeAccount[], selectedIds: string[]): PlatformKey[] {
  const unique = new Set<string>();
  accounts.filter(a => selectedIds.includes(a.id)).forEach(a => unique.add(a.platform.toLowerCase()));
  return Array.from(unique).filter(isPlatformKey);
}

export type EffectiveValues = {
  caption: string;
  media: string[];
  placement: Placement;
  hasPhoto: boolean;
  hasVideo: boolean;
};

export function getEffectiveForPlatform(state: FormState, platform: PlatformKey): EffectiveValues {
  const override = state.platforms[platform];
  const config = (override?.config ?? DEFAULT_CONFIGS[platform]) as PlatformConfiguration;
  const placement = config.placement ?? "timeline";
  const caption = override?.caption !== undefined ? override.caption ?? "" : state.draft.caption ?? "";

  const media =
    override?.mediaTouched && Array.isArray(override?.media)
      ? (override!.media as string[])
      : (state.draft.media ?? []);

  const hasPhoto = (override?.hasPhoto ?? state.draft.hasPhoto ?? false);
  const hasVideo = (override?.hasVideo ?? state.draft.hasVideo ?? false);

  return { caption, media, placement, hasPhoto, hasVideo };
}

export interface PlatformPost {
  platform: string;
  caption: string;
  media: any;
  posted_at?: string | number;
  metrics: any;
  platform_url: string;
}

export interface SocialAccountFeedMeta {
  cursor: string;
  limit: number;
  next: string;
  has_more: boolean;
}

export interface SocialAccountFeedResponse {
  data: PlatformPost[];
  meta: SocialAccountFeedMeta;
}
